import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Zap,
  TrendingUp,
  FileCheck,
  Brain,
  Clock,
  CheckCircle,
  Award,
  Users,
  Target,
  BarChart3,
  Search,
  Download,
  Play,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Sparkles,
  Globe,
  Lock,
  CheckCircle2,
  LogIn
} from 'lucide-react';
import Logo from '../../components/Logo';

const MarketingPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const stats = [
    { value: '10,000+', label: 'Scans Completed', icon: Search },
    { value: '18', label: 'Framework Standards', icon: Shield },
    { value: '99.9%', label: 'Accuracy Rate', icon: Award },
    { value: '50+', label: 'Federal Agencies', icon: Users }
  ];

  const features = [
    {
      icon: Search,
      title: 'Automated Compliance Scanning',
      description: 'Multi-scanner approach evaluates 18 frameworks including NIST, BOD 22-01, Section 508, CMMC, and more. Get comprehensive results in minutes.',
      gradient: 'from-blue-500 to-indigo-600',
      stats: '200+ Controls Evaluated'
    },
    {
      icon: Brain,
      title: 'AI-Powered Comparative Analysis',
      description: 'GPT-4 analyzes compliance gaps across multiple sites with actionable recommendations. Identify patterns and systemic issues instantly.',
      gradient: 'from-purple-500 to-pink-600',
      stats: 'Powered by GPT-4'
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Monitoring Dashboard',
      description: 'Track compliance scores, critical issues, and remediation timelines in one place. Beautiful visualizations make data actionable.',
      gradient: 'from-emerald-500 to-green-600',
      stats: 'Live Updates'
    },
    {
      icon: FileCheck,
      title: 'Intelligent Report Generation',
      description: 'Generate executive-ready PDF reports with color-coded insights in seconds. Export to markdown, CSV, or JSON formats.',
      gradient: 'from-orange-500 to-red-600',
      stats: 'One-Click Export'
    }
  ];

  const frameworks = [
    { name: 'NIST Cybersecurity Framework', abbr: 'NIST-CSF' },
    { name: 'CISA BOD 22-01', abbr: 'BOD-22-01' },
    { name: 'Section 508 / WCAG 2.1 AA', abbr: 'WCAG' },
    { name: '21st Century IDEA', abbr: '21C-IDEA' },
    { name: 'NIST SP 800-53', abbr: 'NIST-800-53' },
    { name: 'FedRAMP Standards', abbr: 'FedRAMP' },
    { name: 'FISMA Compliance', abbr: 'FISMA' },
    { name: 'USWDS Guidelines', abbr: 'USWDS' },
    { name: 'Digital Gov Strategy', abbr: 'DGS' },
    { name: 'Privacy Act', abbr: 'Privacy' },
    { name: 'BOD 18-01', abbr: 'BOD-18-01' },
    { name: 'OMB M-15-13', abbr: 'OMB' },
    { name: 'HTTPS Enforcement', abbr: 'HTTPS' },
    { name: 'DNS Security', abbr: 'DNSSEC' },
    { name: 'Email Security', abbr: 'Email-Sec' },
    { name: 'Third-Party Services', abbr: '3rd-Party' }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Enter Website URL',
      description: 'Simply paste any government website URL and our scanners begin automated assessment',
      icon: Globe
    },
    {
      step: 2,
      title: 'Review AI Analysis',
      description: 'Get color-coded compliance scores, critical issues, and framework-specific insights',
      icon: Brain
    },
    {
      step: 3,
      title: 'Take Action',
      description: 'Download reports, track fixes, and compare multiple sites with AI recommendations',
      icon: Target
    }
  ];

  const testimonials = [
    {
      quote: "GOVCOM transformed our compliance workflow. What took weeks now takes hours. The AI insights are invaluable.",
      author: "Sarah Johnson",
      role: "Chief Information Security Officer",
      agency: "Federal Technology Agency"
    },
    {
      quote: "The comparative analysis feature saved us countless hours. We can now benchmark our entire web portfolio instantly.",
      author: "Michael Chen",
      role: "IT Compliance Director",
      agency: "Department of Digital Services"
    },
    {
      quote: "Finally, a compliance tool that speaks our language. The reports are executive-ready and the data is actionable.",
      author: "Dr. Emily Rodriguez",
      role: "Deputy CTO",
      agency: "Cybersecurity Division"
    }
  ];

  const faqs = [
    {
      question: 'What is GOVCOM and what does it do?',
      answer: 'GOVCOM (Government Compliance) is an automated compliance assessment platform that uses 25+ specialized scanners to evaluate government websites against 18 federal compliance frameworks. It generates comprehensive reports showing your compliance status across 200+ controls, with AI-powered explanations and remediation guidance.'
    },
    {
      question: 'How long does a scan take?',
      answer: 'A complete scan takes 2-5 minutes to execute all 25 scanners using Docker containers. Once scanning is complete, generating a compliance report across all 18 frameworks takes an additional 30-60 seconds. You can view results immediately after the report is generated.'
    },
    {
      question: 'Which compliance frameworks are supported?',
      answer: 'We support 18 major federal frameworks: NIST Cybersecurity Framework, NIST SP 800-53, Section 508, WCAG 2.1 AA, 21st Century IDEA Act, BOD 18-01 (Email Security), BOD 22-01 (Vulnerability Management), OMB M-15-13 (HTTPS), OMB M-17-06, Web Security Configuration, Privacy Act, USWDS, HTTPS Enforcement, DNS Security (DNSSEC), Email Security, Third-Party Services compliance, CMMC, and TIC 2.0.'
    },
    {
      question: 'How many controls are evaluated in each scan?',
      answer: 'Each scan evaluates over 200 individual security and compliance controls across all 18 frameworks. This includes security vulnerabilities, accessibility issues, HTTPS configuration, email authentication, design standards, performance metrics, and more. Each control includes detailed evidence from our 25+ scanners.'
    },
    {
      question: 'Can I scan multiple websites at once?',
      answer: 'Yes! You can upload a CSV file with multiple government website URLs for bulk scanning. Each site will be scanned independently with its own scan ID and compliance report. This is perfect for agencies managing multiple domains or conducting organization-wide assessments.'
    },
    {
      question: 'What is the AI Chat feature?',
      answer: 'Our AI Chat feature uses GPT-4 to answer questions about your specific scan results in real-time. Ask questions like "What are my critical security issues?" or "How do I fix DMARC failures?" and get contextual answers based on your actual scan data. Conversations are saved for reference and responses are cached for performance.'
    },
    {
      question: 'How do I understand technical scan findings?',
      answer: 'Every failed control has an "Explain" button that uses AI to translate technical findings into plain English. You\'ll get a clear explanation of what\'s wrong, why it matters, how to fix it, and a recommended timeline. This makes the platform accessible to non-technical stakeholders and executives.'
    },
    {
      question: 'What scanners does GOVCOM use?',
      answer: 'GOVCOM orchestrates 25+ specialized scanners including: OWASP ZAP and Nuclei (security vulnerabilities), Pa11y and Lighthouse (accessibility), SSLyze and PSHTT (HTTPS/SSL), TrustyMail (email security), USWDS detector (design standards), Wappalyzer (technology stack), and many more. All scanners run in parallel using Docker for speed and isolation.'
    },
    {
      question: 'Who can access my scan data?',
      answer: 'GOVCOM uses multi-tenant architecture with role-based access control. Standard users see only their own scans. Organization admins see all scans within their agency. Global admins can manage multiple organizations. Superadmins have full system access. All data is isolated by organization with enterprise-grade security.'
    },
    {
      question: 'Can I export compliance reports?',
      answer: 'Yes! Reports can be generated on-demand from any completed scan and viewed in the dashboard. You can regenerate reports anytime from existing scan data without re-scanning. Export formats include comprehensive HTML dashboards with interactive charts, and PDF export is planned for future releases.'
    },
    {
      question: 'How accurate are the scan results?',
      answer: 'Our multi-scanner approach achieves industry-leading accuracy by running 25+ specialized tools simultaneously and cross-validating results. Each framework evaluation is based on official government standards and requirements. The AI explanations use your actual scan data as context, ensuring highly accurate and relevant remediation guidance.'
    },
    {
      question: 'What happens to my scan data?',
      answer: 'Raw scan results are permanently stored in MongoDB and preserved even if you delete generated reports. This allows you to regenerate reports anytime, track compliance over time, and maintain a historical record. You can delete reports without losing the underlying scan data. All data is securely stored with organization-level isolation.'
    },
    {
      question: 'Can I track compliance improvements over time?',
      answer: 'Yes! Every scan creates a timestamped record with complete results. You can run new scans quarterly or after making fixes to track your compliance progress. The dashboard shows current compliance rates, and you can compare scan results over time to demonstrate improvements to stakeholders and auditors.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes. We use enterprise-grade security including JWT authentication with HTTP-only cookies (XSS protection), bcrypt password hashing, Google OAuth SSO integration, role-based access control, and multi-tenant data isolation. All scans run in isolated Docker containers. We follow federal security best practices and government standards.'
    }
  ];

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Simulate form submission (replace with actual API call)
    setTimeout(() => {
      alert('Thank you for contacting us! We will get back to you within 24 hours.');
      setContactForm({
        name: '',
        email: '',
        organization: '',
        message: ''
      });
      setSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
      `}</style>

      {/* Navigation */}
      <nav className="relative z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              {/* <Logo size={42} />
              <span className="ml-2 text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                GOVCOM
              </span> */}
               <img 
    src="/images/Logo_low.png" 
    alt="GOVCOM" 
    className="h-10 w-auto"
  />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 font-medium transition">Features</a>
              <a href="#how-it-works" className="text-gray-700 hover:text-blue-600 font-medium transition">How It Works</a>
              <a href="#faq" className="text-gray-700 hover:text-blue-600 font-medium transition">FAQ</a>
              <a href="#contact" className="text-gray-700 hover:text-blue-600 font-medium transition">Contact</a>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition flex items-center"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition shadow-lg"
              >
                Start Free Scan
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-blue-600 mr-2" />
              <span className="text-sm font-semibold text-blue-600">AI-Powered Compliance Platform</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-black text-gray-900 mb-6 leading-tight">
              Government Compliance
              <br />
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Made Simple
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              Automated framework assessments, real-time monitoring, and intelligent insights for federal websites. Powered by AI.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition shadow-2xl transform hover:scale-105 flex items-center"
              >
                <Zap className="w-5 h-5 mr-2" />
                Start Free Scan Now
              </button>
              <button className="px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition shadow-lg flex items-center">
                <Play className="w-5 h-5 mr-2" />
                Watch Demo (2 min)
              </button>
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                NIST Compliant
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                WCAG 2.1 AA
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                FedRAMP Ready
              </div>
            </div>
          </div>

          {/* Dashboard Preview */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl blur-3xl opacity-20"></div>
            <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden">
                <div className="bg-gray-800 px-4 py-3 flex items-center gap-2 border-b border-gray-700">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 backdrop-blur-xl rounded-xl border border-emerald-500/30 p-4">
                      <div className="text-emerald-300 text-sm font-semibold mb-1">Compliance Score</div>
                      <div className="text-4xl font-black text-white">85%</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-xl rounded-xl border border-blue-500/30 p-4">
                      <div className="text-blue-300 text-sm font-semibold mb-1">Controls Passed</div>
                      <div className="text-4xl font-black text-white">42/50</div>
                    </div>
                    <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 backdrop-blur-xl rounded-xl border border-red-500/30 p-4">
                      <div className="text-red-300 text-sm font-semibold mb-1">Critical Issues</div>
                      <div className="text-4xl font-black text-white">3</div>
                    </div>
                  </div>
                  <div className="h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl border border-white/10 flex items-center justify-center">
                    <BarChart3 className="w-16 h-16 text-white/50" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-gray-600 font-semibold mb-4">Trusted by Federal Agencies</p>
            <div className="flex items-center justify-center gap-8 opacity-50">
              <Shield className="w-12 h-12 text-gray-600" />
              <Globe className="w-12 h-12 text-gray-600" />
              <Award className="w-12 h-12 text-gray-600" />
              <Lock className="w-12 h-12 text-gray-600" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="flex items-center justify-center mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="text-4xl font-black text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Powerful Features for Federal Compliance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to maintain and prove compliance across all major government frameworks
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition border border-gray-100 hover:border-gray-200">
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
                  <div className="inline-flex items-center text-sm font-semibold text-blue-600">
                    {feature.stats}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Frameworks Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              18 Framework Standards
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive coverage of federal compliance requirements
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {frameworks.map((framework, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow hover:shadow-lg transition text-center border border-gray-100">
                <div className="text-lg font-bold text-gray-900 mb-2">{framework.abbr}</div>
                <div className="text-sm text-gray-600">{framework.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="relative py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From scan to action in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="relative text-center">
                  <div className="relative inline-flex items-center justify-center mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <Icon className="w-12 h-12 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-blue-50">
                      <span className="text-lg font-black text-blue-600">{step.step}</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  {idx < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-full w-full">
                      <ArrowRight className="w-8 h-8 text-gray-300 mx-auto" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Trusted by Federal Leaders
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what compliance professionals are saying about GOVCOM
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle2 key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <div className="font-bold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                  <div className="text-sm text-blue-600 font-medium">{testimonial.agency}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Pricing Section */}
      {/* <section id="pricing" className="relative py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your agency's needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, idx) => (
              <div key={idx} className={`relative bg-white rounded-2xl p-8 shadow-lg border-2 ${tier.popular ? 'border-blue-600' : 'border-gray-200'}`}>
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-bold px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-5xl font-black text-gray-900">{tier.price}</span>
                    <span className="text-gray-600">/{tier.period}</span>
                  </div>
                  <p className="text-gray-600">{tier.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 rounded-xl font-bold transition ${tier.popular ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}>
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section id="faq" className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about GOVCOM
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition"
                >
                  <span className="text-lg font-bold text-gray-900 pr-4">{faq.question}</span>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Contact Support Section */}
      <section id="contact" className="relative py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600">
              Have questions? Our team is here to help you with your compliance needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8 max-w-2xl mx-auto">
            <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 text-center hover:shadow-xl transition">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div className="text-sm font-bold text-gray-900 mb-1">Response Time</div>
              <div className="text-xs text-gray-600">Within 24 hours</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 text-center hover:shadow-xl transition">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <FileCheck className="w-5 h-5 text-white" />
              </div>
              <div className="text-sm font-bold text-gray-900 mb-1">Email</div>
              <div className="text-xs text-gray-600">support@govc.gov</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-6">
              <h3 className="text-xl font-bold text-white mb-1">Send Us a Message</h3>
              <p className="text-sm text-blue-100">Fill out the form below and we'll get back to you soon</p>
            </div>

            <form onSubmit={handleContactSubmit} className="p-6">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    required
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none transition text-gray-900 text-sm"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    required
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none transition text-gray-900 text-sm"
                    placeholder="john.doe@agency.gov"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="organization" className="block text-sm font-bold text-gray-900 mb-2">
                  Organization / Agency
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={contactForm.organization}
                  onChange={handleContactChange}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none transition text-gray-900 text-sm"
                  placeholder="Department of Technology"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none transition resize-none text-gray-900 text-sm"
                  placeholder="Tell us about your compliance needs or ask any questions..."
                />
              </div>

              <div className="flex items-start gap-2 mb-6">
                <input
                  type="checkbox"
                  id="consent"
                  required
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                />
                <label htmlFor="consent" className="text-xs text-gray-600 leading-relaxed">
                  I agree to be contacted by GOVCOM regarding my inquiry and understand that my information will be handled according to the privacy policy.
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-base rounded-xl hover:from-blue-700 hover:to-purple-700 transition shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending Message...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                {/* <Logo size={34} />
                <span className="ml-2 text-lg font-bold text-white">GOVCOM</span> */}
                <img 
                   src="/images/Logo_low.png" 
                    alt="GOVCOM" 
                   className="h-10 w-auto"
                    />
              </div>
              <p className="text-sm">
                AI-powered government compliance made simple.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-3">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                {/* <li><a href="#pricing" className="hover:text-white">Pricing</a></li> */}
                <li><a href="#" className="hover:text-white">Demo</a></li>
                <li><a href="#" className="hover:text-white">API Docs</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-3">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#contact" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
                <li><a href="#" className="hover:text-white">Compliance</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; 2025 GOVCOM. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingPage;
